import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import { DiggingLocationDTO } from '../DTOs/Production/DiggingLocationDTO'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { StoppageSubTypeDto } from '../DTOs/Stoppage/StoppageSubTypeDto'
import { StoppageTypeDto } from '../DTOs/Stoppage/StoppageTypeDto'
import { queryClient } from '../queryClient'
import { setProvisionalShift } from '../state/reducers/shiftReducer'
import { store } from '../state/store'
import type { DoubleShift } from '../types/Shift'
import { appendCachedStoppage } from './patchCachedStoppages'
import { plantStoppagesQueryKey } from './plantStoppagesCache'
import { computeShiftId } from './computeShiftId'
import { diggingPatternMutations } from './diggingPatternMutations'
import { nextTempId } from './tempId'
import { nextShiftAfter, shiftStart } from './shiftBands'
import { unfilteredStoppagesListCacheKey } from "./stoppagesListCacheKey"

const OPERATIONS_KEY = ['dataTable', 'operations', 'stoppageCalcs'] as const
const DIGGER_LOCATIONS_KEY = ['dataTable', 'chatree', 'diggerLocations'] as const

const COPYABLE_OPTION_KEYS = [
  ['chatree', 'plants'],
  ['production', 'plants'],
  ['production', 'locationOptions'],
  ['references', 'blastmasterIds'],
  ['geology', 'rehandleTrucks'],
  ['geology', 'rehandleFromLocations'],
  ['geology', 'rehandleToLocations'],
  ['geology', 'crushingFromLocations'],
  ['geology', 'crushingToLocations'],
  ['geology', 'productionTrucks'],
] as const

const OPERATION_OPTION_KEYS = [
  ['geology', 'rehandleDiggers'],
  ['geology', 'crushingLoaders'],
] as const

const EMPTY_LIST_KEYS = [
  (d: string, s: DoubleShift) => unfilteredStoppagesListCacheKey(d, s),
  (d: string, s: DoubleShift) => ['dataTable', 'geology', 'rehandles', d, s, 'all', false, null],
  (d: string, s: DoubleShift) => ['dataTable', 'geology', 'crushing', d, s, 'all', false, null],
  (d: string, s: DoubleShift) => ['dataTable', 'chatree', 'diggingLoadingSummary', d, s, 'all', false, null],
] as const

export const EARLIEST_COMPLETION_MINUTES_BEFORE_START = 45

export interface ProvisionalShiftResult {
  date: string
  shift: DoubleShift
  shiftId: number
  /** Machines carried into the new shift. Zero means nothing was cached for the completed shift. */
  operationsCarried: number
  patternsCarried: number
}

function isEmptyList(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (Array.isArray(value)) return value.length === 0
  const rows = (value as { data?: unknown }).data
  return Array.isArray(rows) && rows.length === 0
}

function seedIfEmpty(key: unknown[], value: unknown): boolean {
  if (!isEmptyList(queryClient.getQueryData(key))) return false
  queryClient.setQueryData(key, value)
  return true
}

function retargetKey(key: unknown[], date: string, shift: DoubleShift, dateIndex: number): unknown[] {
  const next = [...key]
  next[dateIndex] = date
  next[dateIndex + 1] = shift
  return next
}

async function seedOperations(fromDate: string, fromShift: string, to: ProvisionalShiftResult): Promise<number> {
  const sources = queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: [...OPERATIONS_KEY, fromDate, fromShift],
  })
  let carried = 0

  for (const [key, page] of sources) {
    const plants = page?.data ?? []
    if (!plants.length) continue

    const rows: StoppageCalc[] = []
    for (const plant of plants) {
      const stillDown = !!plant.status && plant.status !== 'UP'
      rows.push(new StoppageCalc({
        operationId: await nextTempId(),
        shiftId: to.shiftId,
        plantId: plant.plantId,
        machineName: plant.machineName,
        status: stillDown ? plant.status : 'UP',
        statusDescription: stillDown ? plant.statusDescription : undefined,
        stoppageColourHub: stillDown ? plant.stoppageColourHub : undefined,
        stoppageId: stillDown ? plant.stoppageId : undefined,
        currentStoppageTypeId: stillDown ? plant.currentStoppageTypeId : undefined,
        currentStoppageSubTypeId: stillDown ? plant.currentStoppageSubTypeId : undefined,
        timeIn: stillDown ? plant.timeIn : undefined,
        startEngineHours: plant.endEngineHours ?? plant.startEngineHours ?? 0,
        locationId: plant.locationId,
      }))
    }

    const seeded = seedIfEmpty(
      retargetKey(key as unknown[], to.date, to.shift, 3),
      new PagedResult(rows, rows.length, Math.max(1, rows.length), 1, 1000),
    )
    if (seeded) carried = Math.max(carried, rows.length)
  }

  return carried
}

function seedShiftScopedOptions(fromDate: string, fromShift: string, to: ProvisionalShiftResult): void {
  for (const prefix of COPYABLE_OPTION_KEYS) {
    for (const [key, value] of queryClient.getQueriesData({ queryKey: [...prefix, fromDate, fromShift] })) {
      if (value === undefined) continue
      seedIfEmpty(retargetKey(key as unknown[], to.date, to.shift, prefix.length), value)
    }
    const dateOnly = queryClient.getQueryData([...prefix, fromDate])
    if (dateOnly !== undefined) seedIfEmpty([...prefix, to.date], dateOnly)
  }

  for (const buildKey of EMPTY_LIST_KEYS) {
    seedIfEmpty(buildKey(to.date, to.shift) as unknown[], PagedResult.empty())
  }
}

function seedCarriedStoppages(to: ProvisionalShiftResult): void {
  const carried: StoppageDto[] = []

  for (const [key, page] of queryClient.getQueriesData<PagedResult<StoppageDto>>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', to.date, to.shift],
  })) {
    if (typeof (key as unknown[])[5] !== 'number') continue
    for (const row of page?.data ?? []) {
      if (row?.stoppageId && !carried.some(existing => existing.stoppageId === row.stoppageId)) carried.push(row)
    }
  }

  for (const stoppage of carried) {
    appendCachedStoppage(stoppage, to.date, to.shift)
  }
}

function seedOperationOptions(fromDate: string, fromShift: string, to: ProvisionalShiftResult): void {
  const operations = provisionalOperations(to)
  if (!operations.length) return

  const byPlantId = new Map<number, number>()
  const byPlantRef = new Map<string, number>()
  for (const op of operations) {
    if (op.plantId) byPlantId.set(op.plantId, op.operationId)
    if (op.machineName) byPlantRef.set(op.machineName, op.operationId)
  }

  for (const prefix of OPERATION_OPTION_KEYS) {
    const source = queryClient.getQueryData<Record<string, unknown>[]>([...prefix, fromDate, fromShift])
    if (!Array.isArray(source) || !source.length) continue

    const remapped = source
      .map(option => {
        const plantId = option.plantId as number | undefined
        const plantRef = option.plantRef as string | undefined
        const operationId = (plantId ? byPlantId.get(plantId) : undefined)
          ?? (plantRef ? byPlantRef.get(plantRef) : undefined)
        return operationId ? { ...option, operationId } : null
      })
      .filter((option): option is NonNullable<typeof option> => option !== null)

    if (remapped.length) seedIfEmpty([...prefix, to.date, to.shift], remapped)
  }
}

function seedNestedStoppages(fromDate: string, fromShift: string, to: ProvisionalShiftResult): void {
  const openByPlant = new Map<number, StoppageDto[]>()
  for (const [key, page] of queryClient.getQueriesData<PagedResult<StoppageDto>>({
    queryKey: ['dataTable', 'operations', 'plantStoppages', fromDate, fromShift],
  })) {
    const plantId = (key as unknown[])[5]
    if (typeof plantId !== 'number') continue
    const open = (page?.data ?? []).filter(row => row?.endTime == null)
    if (open.length) openByPlant.set(plantId, open)
  }

  for (const op of provisionalOperations(to)) {
    if (!op.plantId) continue
    const stillDown = !!op.status && op.status !== 'UP'
    const rows = stillDown
      ? openByPlant.get(op.plantId) ?? [stoppageFromOperation(op)].filter((row): row is StoppageDto => row !== null)
      : []
    seedIfEmpty(
      plantStoppagesQueryKey(to.date, to.shift, op.plantId, op.status),
      new PagedResult(rows, rows.length, Math.max(1, rows.length), 1, 5),
    )
  }
}

function stoppageFromOperation(op: StoppageCalc): StoppageDto | null {
  if (!op.stoppageId || !op.plantId) return null

  return new StoppageDto({
    stoppageId: op.stoppageId,
    plantId: op.plantId,
    startTime: op.timeIn!,
    stoppageTypeId: op.currentStoppageTypeId ?? 0,
    stoppageSubTypeId: op.currentStoppageSubTypeId ?? 0,
    incident: false,
    stoppageSubType: new StoppageSubTypeDto({
      stoppageSubTypeId: op.currentStoppageSubTypeId ?? 0,
      stoppageSubType: (op.statusDescription ?? '').split(' - ').slice(1).join(' - ') || (op.statusDescription ?? ''),
      stoppageTypeId: op.currentStoppageTypeId ?? 0,
    }),
    stoppageType: new StoppageTypeDto({
      stoppageTypeId: op.currentStoppageTypeId ?? 0,
      stoppageType: op.status ?? '',
      colourHex: op.stoppageColourHub,
    }),
  })
}

function provisionalOperations(to: ProvisionalShiftResult): StoppageCalc[] {
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: [...OPERATIONS_KEY, to.date, to.shift],
  })) {
    const rows = page?.data ?? []
    if (rows.length) return rows
  }
  return []
}

async function carryDiggingPatterns(
  fromDate: string,
  fromShift: string,
  to: ProvisionalShiftResult,
): Promise<number> {
  const upPlantIds = new Set<number>()
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: [...OPERATIONS_KEY, fromDate, fromShift],
  })) {
    for (const row of page?.data ?? []) {
      if (row?.status === 'UP' && row.plantId) upPlantIds.add(row.plantId)
    }
  }

  const startTime = shiftStart(to.date, to.shift)
  let carried = 0

  for (const [key, page] of queryClient.getQueriesData<PagedResult<DiggingLocationDTO>>({
    queryKey: [...DIGGER_LOCATIONS_KEY, fromDate, fromShift],
  })) {
    const diggerId = (key as unknown[])[5]
    if (typeof diggerId !== 'number' || !upPlantIds.has(diggerId)) continue

    const locations = page?.data ?? []
    if (!locations.length) continue

    const targetKey = retargetKey(key as unknown[], to.date, to.shift, 3)
    if (queryClient.getQueryData(targetKey) !== undefined) continue

    const mostRecent = locations.reduce((latest, candidate) =>
      (candidate.startTime > latest.startTime ? candidate : latest))

    const carriedLocation = new DiggingLocationDTO({ ...mostRecent, startTime, rowVersion: undefined })
    const response = await diggingPatternMutations.insertDiggingLocation(
      DateTime.fromISO(to.date), to.shift, diggerId, carriedLocation, to.shiftId,
    )
    if (!response?.data) continue

    queryClient.setQueryData(targetKey, new PagedResult([response.data], 1, 1, 1, 5))
    carried++
  }

  return carried
}

export function minutesBeforeNextShiftStart(fromDate: string, fromShift: string): number {
  const next = nextShiftAfter(fromDate, fromShift)
  return shiftStart(next.date, next.shift).diff(DateTime.now(), 'minutes').minutes
}

export async function createProvisionalNextShift(
  fromDate: string,
  fromShift: string,
): Promise<ProvisionalShiftResult> {
  const next = nextShiftAfter(fromDate, fromShift)
  const result: ProvisionalShiftResult = {
    date: next.date,
    shift: next.shift,
    shiftId: computeShiftId(next.date, next.shift),
    operationsCarried: 0,
    patternsCarried: 0,
  }

  result.operationsCarried = await seedOperations(fromDate, fromShift, result)
  seedShiftScopedOptions(fromDate, fromShift, result)
  seedOperationOptions(fromDate, fromShift, result)
  seedNestedStoppages(fromDate, fromShift, result)
  seedCarriedStoppages(result)
  result.patternsCarried = await carryDiggingPatterns(fromDate, fromShift, result)

  store.dispatch(setProvisionalShift({ date: result.date, shift: result.shift }))

  return result
}
